import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Text from "components/Text";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Card from "components/Card";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import CommentIcon from "assets/images/comment_icon.svg";
import alertIcon from "assets/images/redAlert.svg";
import dotIcon from "assets/images/dots-icon.svg";
import {
  officePolicyStatusOptions,
  prioritiesOptions,
} from "../../../../../constants";
import styles from "../../OfficePolicyManagement.module.scss";
import useCardModule from "../../hooks/useCardModule";
import StaffMarkAsCompleteModal from "../../Modals/StaffMarkAsCompleteModal";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import { formatLocalDateTime } from "utils";

function getStatusLabel(statusId) {
  if (statusId == null) return "";
  const option = (officePolicyStatusOptions || []).find(
    (o) => Number(o.value) === Number(statusId),
  );
  return option?.label ?? "";
}

function getPriorityLabel(priorityId) {
  if (priorityId == null) return "";
  const option = (prioritiesOptions || []).find(
    (o) => Number(o.id) === Number(priorityId),
  );
  return option?.name ?? "";
}

const StaffPolicyCard = ({
  t,
  myOfficePolicy,
  getButtonClass,
  handleActionOfficePolicy,
  className,
  isArchive,
  handleArchiveApi,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isOverdue, setIsOverdue] = useState(false);
  const [officesPopup, setOfficesPopup] = useState({
    isOpen: false,
    offices: [],
  });
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const cardWrapperRef = useRef(null);
  const popupTriggerRef = useRef(null);
  const popupRef = useRef(null);
  const history = useHistory();

  const {
    data: { isCompletionModalOpen },
    methods: { handleCardClick, handleCompletionModal },
  } = useCardModule();

  const toggle = () => setDropdownOpen((prev) => !prev);

  useEffect(() => {
    const dueDate = moment.utc(myOfficePolicy?.dueDate);
    const status = getStatusLabel(myOfficePolicy?.status);
    if (
      dueDate.isBefore(moment.utc()) &&
      ["Pending", "Accepted", "Started"].includes(status)
    ) {
      setIsOverdue(true);
    } else {
      setIsOverdue(false);
    }
  }, [myOfficePolicy?.status, myOfficePolicy?.dueDate]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        officesPopup.isOpen &&
        popupRef.current &&
        popupTriggerRef.current &&
        !popupRef.current.contains(event.target) &&
        !popupTriggerRef.current.contains(event.target)
      ) {
        setOfficesPopup({
          isOpen: false,
          offices: [],
        });
        setInvitePopupPosition({ top: 0, right: 0 });
      }
    };

    if (officesPopup.isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [officesPopup.isOpen]);

  /** Maps priority id to style key (prioritiesOptions: 1=Low, 2=Medium, 3=High). */
  const getPriorityClass = (priorityId) => {
    const keyMap = { 3: "low", 2: "medium", 1: "high" };
    const key = keyMap[priorityId] ?? "low";
    return {
      wrapperClass: styles[`${key}-priority-wrapper`] ?? "",
      dotClass: styles[`${key}-priority-dot`] ?? "",
      textClass: styles[`${key}-priority-text`] ?? "",
    };
  };

  const { wrapperClass, dotClass, textClass } = getPriorityClass(
    myOfficePolicy?.priority,
  );

  const handleViewMoreOffices = useCallback((e, offices) => {
    e.stopPropagation();
    let top = 40;
    let right = 40;
    if (cardWrapperRef.current) {
      const containerRect = cardWrapperRef.current.getBoundingClientRect();
      const popupEstimatedHeight = 250;
      const popupEstimatedWidth = 280;
      // Get click position relative to viewport
      const clickX = e.clientX;
      const clickY = e.clientY;
      // Calculate position relative to container
      top = clickY - containerRect.top + 18;
      right = containerRect.right - clickX;
      // Adjust if popup would go below viewport
      if (
        clickY + popupEstimatedHeight > window.innerHeight &&
        clickY > popupEstimatedHeight
      ) {
        top = clickY - containerRect.top - popupEstimatedHeight + 113;
      }
      // Adjust if popup would overflow container width
      if (right + popupEstimatedWidth > containerRect.width) {
        right = Math.max(10, containerRect.width - popupEstimatedWidth - 10);
      }
      if (right < 0) right = 10;
    }
    setInvitePopupPosition({ top, right });
    setOfficesPopup({
      isOpen: true,
      offices: Array.isArray(offices) ? offices : [],
    });
  }, []);

  const handleInvitePopupToggle = useCallback((e) => {
    e?.stopPropagation?.();
    setOfficesPopup({
      isOpen: false,
      offices: [],
    });
    setInvitePopupPosition({ top: 0, right: 0 });
  }, []);

  const officeList = Array.isArray(myOfficePolicy?.office)
    ? myOfficePolicy.office
    : [];
  const officeCount = officeList.length;

  return (
    <Fragment>
      <Card
        className={className + " position-relative"}
        onClick={(e) => handleCardClick(e, myOfficePolicy, history)}
        ref={cardWrapperRef}
      >
        {isOverdue && (
          <div className={styles["alert_text_wrapper"]}>
            <img src={alertIcon} alt="alert" />
            <Text size="11px" weight="400" color="#EE4F4F">
              {t("officePolicyManagement.policyOverdue")}
            </Text>
          </div>
        )}
        <div className={styles["card_top_wrapper"]}>
          <div className={styles["status_priority_date_wrapper"]}>
            <button
              className={getButtonClass(getStatusLabel(myOfficePolicy?.status))}
            >
              {getStatusLabel(myOfficePolicy?.status)}
            </button>
            <div
              className={`${styles["circle_priority_text_wrapper"]} ${wrapperClass}`}
            >
              <span className={`${styles["circular_dot"]} ${dotClass}`} />
              <p className={`${styles["priority_text"]} ${textClass}`}>
                {getPriorityLabel(myOfficePolicy?.priority)}
              </p>
            </div>
            <div className={styles["date"]}>
              {formatLocalDateTime(myOfficePolicy?.createdAt, "MMM D, YYYY")}
            </div>
          </div>
          <div>
            <Dropdown
              isOpen={dropdownOpen}
              toggle={toggle}
              className="interactive-element"
            >
              <DropdownToggle
                caret={false}
                tag="div"
                className="dropdown-btn px-3"
              >
                <img src={dotIcon} alt="icon" />
              </DropdownToggle>
              <DropdownMenu end className={styles["custom_dropdown_wrapper"]}>
                {getStatusLabel(myOfficePolicy?.status) === "Pending" && (
                  <>
                    <DropdownItem className={styles["dropdown_list"] + " p-0"}>
                      <p
                        className={styles["dropdown_link"] + " mb-0"}
                        onClick={() =>
                          handleActionOfficePolicy(myOfficePolicy?.id, "Accept")
                        }
                      >
                        {t("officePolicyManagement.accept")}
                      </p>
                    </DropdownItem>
                  </>
                )}
                {getStatusLabel(myOfficePolicy?.status) === "Accepted" && (
                  <DropdownItem className={styles["dropdown_list"] + " p-0"}>
                    <p
                      className={styles["dropdown_link"] + " mb-0"}
                      onClick={() =>
                        handleActionOfficePolicy(myOfficePolicy?.id, "Start")
                      }
                    >
                      {t("officePolicyManagement.start")}
                    </p>
                  </DropdownItem>
                )}
                {getStatusLabel(myOfficePolicy?.status) === "Started" && (
                  <DropdownItem className={styles["dropdown_list"] + " p-0"}>
                    <p
                      className={styles["dropdown_link"] + " mb-0"}
                      onClick={() => {
                        if (myOfficePolicy?.isPolicyAcknowledgementEnabled) {
                          handleCompletionModal();
                        } else {
                          handleActionOfficePolicy(
                            myOfficePolicy?.id,
                            "Complete",
                            {},
                          );
                        }
                      }}
                    >
                      {t("officePolicyManagement.markComplete")}
                    </p>
                  </DropdownItem>
                )}
                {!isArchive ? (
                  <DropdownItem className={styles["dropdown_list"] + " p-0"}>
                    <p
                      className={styles["dropdown_link"]}
                      onClick={() =>
                        handleArchiveApi(myOfficePolicy?.id, "Archive")
                      }
                    >
                      {t("officePolicyManagement.archive")}
                    </p>
                  </DropdownItem>
                ) : (
                  <DropdownItem className={styles["dropdown_list"] + " p-0"}>
                    <p
                      className={styles["dropdown_link"]}
                      onClick={() =>
                        handleArchiveApi(myOfficePolicy?.id, "Unarchive")
                      }
                    >
                      {t("officePolicyManagement.unarchive")}
                    </p>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
        <p className={styles["card_main_heading"]}>{myOfficePolicy?.title}</p>
        <div className={styles["office_assignby_wrapper"]}>
          <div className={styles["office_name"]}>
            <Text size="12px" weight="400" color="#6F7788">
              {t("officePolicyManagement.officeName")}
            </Text>
            {officeCount === 0 ? (
              <Text
                size="14px"
                weight="600"
                color="#102C42"
                className={styles.break_word}
              >
                —
              </Text>
            ) : officeCount === 1 ? (
              <Text
                size="14px"
                weight="600"
                color="#102C42"
                className={styles.break_word}
              >
                {officeList[0]?.name ?? ""}
              </Text>
            ) : (
              <button
                ref={popupTriggerRef}
                type="button"
                className={`${styles.break_word} link-btn p-0 border-0 bg-transparent text-primary`}
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#102C42",
                }}
                onClick={(e) => handleViewMoreOffices(e, officeList)}
              >
                {officeCount} {t("officePolicyManagement.offices")}
              </button>
            )}
          </div>
          <div>
            <Text size="12px" weight="400" color="#6F7788">
              {t("officePolicyManagement.assignedBy")}
            </Text>
            <Text size="14px" weight="600" color="#102C42">
              {`${myOfficePolicy?.assignor?.firstName || ""} ${myOfficePolicy?.assignor?.lastName || ""}`}
            </Text>
          </div>
        </div>
        <hr />
        <div className={styles["comment_date_wrapper"]}>
          <div>
            <img
              style={{ display: "inline-block" }}
              src={CommentIcon}
              alt="comments"
              className="mr-1"
            />
            <span className={styles["comment_text"]}>
              {myOfficePolicy?.totalComments}{" "}
              {t("officePolicyManagement.comments")}
            </span>
          </div>
          <div className={styles["comment_text"]}>
            {t("officePolicyManagement.due")}{" "}
            {moment.utc(myOfficePolicy?.dueDate).format("MMM D, YYYY")}
          </div>
        </div>
        {officesPopup.isOpen && officesPopup.offices.length > 0 && (
          <div ref={popupRef}>
            <InviteesPopup
              position={invitePopupPosition}
              handleInvitePopupToggle={handleInvitePopupToggle}
              list={officesPopup.offices}
              onlyOfffice={true}
              isForEvent={true}
            />
          </div>
        )}
      </Card>

      {isCompletionModalOpen &&
        myOfficePolicy?.isPolicyAcknowledgementEnabled && (
          <StaffMarkAsCompleteModal
            issaveEmployeeModalOpen={isCompletionModalOpen}
            officePolicy={myOfficePolicy}
            handleActionOfficePolicy={handleActionOfficePolicy}
            setIsSaveEmployeeModalOpen={handleCompletionModal}
          />
        )}
    </Fragment>
  );
};

export default withTranslation()(StaffPolicyCard);
